// The slot machine (casino.md §4.3).
//
// The machine is a house game with one seat, so the screen carries no
// ring and no turn: the reader picks a stake tier, presses SPIN once, and
// the next `CasinoResult` states the three symbols and what the line
// paid. Between the press and that frame the button is DEAD, because a
// second press would be a second stake against a spin the house has
// already taken.
//
// **The machine holds NO TABLE.** `CasinoPanel` opens this screen from
// the lobby on its own, so every line below draws with
// `slice.table === null`. The tier is the ONE tier the panel keeps for the
// lobby and the machine (R2165): the machine opens on the tier the lobby
// kept, and its pills write the same kept tier. The stake is the minimum
// bet of that tier. A pull is `{kind:'spin', stakeTier}` and the answer is
// a `CasinoResult` with `game == SLOTS` and `tableId == 0`, which is why
// `landed` reads the game and the hand id and never a table id.
//
// **The symbols are indices, never words.** `CasinoResult.reels` carries
// one index per reel into the symbol list of `data/contracts/casino.toml`
// (`CASINO_SYMBOLS`). Each index draws its mark of `symbols.ts`: a stroke
// mark in the club rim, built once (R988, R2165). The text glyphs of the
// first build are gone, because the kraken ❦ read as a floral mark.
//
// **The reels run, then stop left to right** (R2115). While a pull is on
// the wire, each strip holds the symbol list twice and loops through one
// list in `CASINO_REEL_MS`. When the result lands, the strip holds the
// list twice and the landed symbol under it, and the stylesheet carries
// the landed symbol into the window with a slowing travel: reel n takes
// `CASINO_REEL_MS` plus n times `CASINO_REEL_STAGGER_MS`, so all three
// start at once and stop one after another, the last one at `landingMs`
// of `resultcue.ts`. The cell above the landed symbol is always the blank
// stop, the last id of the list, so the stop never shows a symbol that
// nearly came. The verdict, the result tag and the pay line wait for the
// last stop. Nothing here holds a clock: the panel stamps the first
// render of a result (`GameCtx.resultAtMs`). Under reduced motion the
// strip holds the landed cell alone (R541): the result stands at once and
// no travel is needed at all.

import { CASINO_SYMBOLS } from '../../gen/balance';
import type { MapKeyId } from '../../gen/presentation';
import { overlay as S } from '../../gen/strings';
import { CasinoGame, type CasinoResult, type CasinoStake } from '../../gen/proto/aquaphobia';
import { groupThousands } from '../../locale/group';
import { esc } from '../esc';
import { ctl, stakeRow } from './cards';
import { part, type GameCtx, type GameView } from './gameview';
import { pickTier } from './lobby';
import { pct } from './pct';
import { tagSpeech, resultTag, handWord, sayAttrs } from './moment';
import { hasLanded, landingMs } from './landing';
import { ANY_MARK, symbolMark, symbolName } from './symbols';

/** The index of the blank stop: the last id of the list. */
const BLANK = CASINO_SYMBOLS.length - 1;

/** How many reels the machine turns. The wire carries one index each. */
const REELS = 3;

/**
 * True while a spin is on the wire and no result has answered it.
 *
 * It is a flag of its own and never `spinAtMs > 0`: a `performance.now()`
 * reading is zero at the first moment of the page, and a machine that
 * spun then would carry a dead handle for the rest of the session.
 */
let spinning = false;

/** The `performance.now()` of the spin in flight. `spinning` gates it. */
let spinAtMs = 0;

/**
 * The result counter that stood when the spin left. A higher one ends the
 * flight.
 *
 * It is NOT the hand id (R988). The machine holds no table, so the server
 * may carry one id on every pull, and a flight that waited for a
 * different id sat dead until the act timeout ran out — about 24 seconds
 * with the handle refusing every press. `CasinoSlice.resultSeq` rises
 * with every result frame, so a pull always sees its own answer.
 */
let spinFromSeq = 0;

/** How many times this reader pulled the handle in this session. */
let pulls = 0;

/**
 * The stake row the screen spins at: the one kept tier of the lobby and
 * the machine, taken down to a tier the reader can pay (`pickTier`, R2165).
 */
function stakeRowOf(ctx: GameCtx): CasinoStake | null {
  return ctx.rules ? pickTier(ctx.rules, ctx.credits, ctx.tier) : null;
}

/** The tier the screen spins at. */
function tierOf(ctx: GameCtx): number {
  return stakeRowOf(ctx)?.tier ?? 0;
}

/** What one pull costs at the tier in force. */
function stakeOf(ctx: GameCtx): number {
  return stakeRowOf(ctx)?.minBet ?? 0;
}

/**
 * True between the sent intent and the result that answers it.
 *
 * The flight also ends after the seat's own act timeout, which the hello
 * catalog states: a result that never arrives must not leave the handle
 * dead for the rest of the session. This reads state and writes none, so
 * a render pass is free of side effects.
 */
function inFlight(ctx: GameCtx): boolean {
  if (!spinning) return false;
  const r = ctx.slice.result;
  if (r && r.game === CasinoGame.CASINO_GAME_SLOTS
    && ctx.slice.resultSeq > spinFromSeq) {
    return false;
  }
  const capSec = ctx.rules?.actSec ?? 0;
  if (capSec > 0 && ctx.nowMs - spinAtMs > capSec * 1000) return false;
  return true;
}

/** The result the reels show, or null while none stands or one is in flight. */
function landed(ctx: GameCtx): CasinoResult | null {
  const r = ctx.slice.result;
  if (!r || r.game !== CasinoGame.CASINO_GAME_SLOTS) return null;
  if (inFlight(ctx)) return null;
  if (r.reels.length < REELS) return null;
  return r;
}

/** The run cells, built on the first call. */
let runStrip = '';

/**
 * The run cells of one strip: the symbol list twice, in the pack's order.
 * The blank stop is the last id of the list, so it is the cell that stands
 * over the landed symbol as a reel stops. The cells never change, so the
 * strip is built one time.
 */
function runCells(): string {
  if (runStrip !== '') return runStrip;
  let one = '';
  for (let i = 0; i < CASINO_SYMBOLS.length; i += 1) one += `<span class="sym">${symbolMark(i)}</span>`;
  runStrip = one + one;
  return runStrip;
}

/**
 * The three reel boxes (R2115).
 *
 * - A pull on the wire: each strip loops (`run`), the run cells alone.
 * - A result: the run cells and the landed symbol, and the strip travels
 *   to the landed symbol (`spin`). A pull that paid more than its stake
 *   marks the pay line (`paid`), which lights when the last reel stops.
 * - At rest, and under reduced motion: one still cell a reel.
 */
function reelsHtml(ctx: GameCtx): string {
  const r = landed(ctx);
  const flight = inFlight(ctx);
  const blank = symbolMark(BLANK);
  const list = CASINO_SYMBOLS.length;
  let out = '';
  for (let i = 0; i < REELS; i += 1) {
    const face = r ? symbolMark(r.reels[i]) : blank;
    let cells = `<span class="sym">${face}</span>`;
    let mode = '';
    let run = 0;
    if (!ctx.still && flight) {
      cells = runCells();
      mode = ' run';
      run = list;
    } else if (!ctx.still && r) {
      cells = runCells() + cells;
      mode = ' spin';
      run = 2 * list;
    }
    out += `<div class="reel casino-reel${mode}" style="--casino-reel-index: ${i}; --casino-reel-run: ${run}">`
      + `<span class="reel-strip">${cells}</span></div>`;
  }
  // The pay line lights when the last reel stops: the stylesheet waits
  // `--casino-land-ms`, the span `landingMs` states.
  const paid = r && !flight && r.yourDelta > 0;
  const attrs = paid
    ? ` class="reels paid" style="--casino-land-ms: ${landingMs(r, ctx.still)}ms"`
    : ' class="reels"';
  return `<div${attrs}>${out}</div>`;
}

/** One paytable row's line, drawn from the symbol ids its code names. */
function payLine(code: string): string {
  const syms = CASINO_SYMBOLS as readonly string[];
  const three = syms.indexOf(/^(.+)_three$/.exec(code)?.[1] ?? '');
  if (three >= 0) return symbolMark(three).repeat(REELS);
  if (code === 'any_three') return ANY_MARK.repeat(REELS);
  const any = syms.indexOf(/^any_(.+)$/.exec(code)?.[1] ?? '');
  if (any >= 0) return symbolMark(any) + ANY_MARK.repeat(REELS - 1);
  // A row the pack adds later still draws: its wire id stands in for the
  // line until this file learns the shape.
  return esc(code);
}

/** The paytable, one row a line the machine pays. */
function paytableHtml(ctx: GameCtx): string {
  const rows = ctx.rules?.slotPaytable ?? [];
  if (rows.length === 0) return '';
  const body = rows.map((p) => '<tr>'
    + `<td class="line">${payLine(p.code)}</td>`
    + '<td class="x">×</td>'
    + `<td class="pays">${groupThousands(p.pays)}</td>`
    + '</tr>').join('');
  return part('pays', 'class="paytable"', `<tbody>${body}</tbody>`, 'table');
}

/**
 * What the last pull paid, or the line the server wrote for it.
 *
 * It is a part of its own, because it changes with every pull while the
 * reels beside it must keep their travel (`gameview.ts`). It says that
 * the reels run until the last reel stops, then it states the result tag
 * and the line (R2115): the result tag is the reader's move, the stake taken
 * off, so a line that paid back less than the stake reads LOST.
 */
function winHtml(ctx: GameCtx): string {
  if (inFlight(ctx)) return part('win', 'class="win run"', S.slots_reels_run);
  const r = landed(ctx);
  if (!r) return '';
  if (!hasLanded(r, ctx.still, ctx.nowMs, ctx.resultAtMs)) {
    return part('win', 'class="win run"', S.slots_reels_run);
  }
  const [a, b, c] = r.reels;
  let says: string;
  if (r.line) says = esc(r.line);
  else if (r.slotPays <= 0) says = S.slots_no_line;
  else if (a === b && b === c) says = S.slots_three_pay({ symbol: symbolName(a), pays: groupThousands(r.slotPays) });
  else says = S.slots_line_pays({ pays: groupThousands(r.slotPays) });
  const seq = ctx.slice.resultSeq;
  const word = handWord(r.yourDelta);
  return part('win', `class="win"${sayAttrs(seq, `${tagSpeech(r.yourDelta, word)}. ${says}`)}`,
    `${resultTag(r.yourDelta, word, seq)}<span class="say">${says}</span>`);
}

/**
 * The stake pills, one a tier the house runs: one pill group of the
 * choice kit, and a stake over the reader's credits is shut (R2118).
 *
 * The op is `tier`, the op of the lobby's pills: the panel keeps ONE tier
 * for the lobby and the machine (R2165), so a pick here is the tier the
 * lobby shows next, and the machine opens on the tier the lobby kept.
 */
function pillsHtml(ctx: GameCtx, tier: number): string {
  const stakes = ctx.rules?.stakes ?? [];
  if (stakes.length === 0) return '';
  const list = stakes.map((s) => ({
    id: `slot-${s.tier}`,
    label: S.casino_tier_pill({ min: groupThousands(s.minBet) }),
    payable: s.minBet <= ctx.credits,
    data: { op: 'tier', tier: String(s.tier) },
  }));
  return part('pills', 'class="pills"', stakeRow(list, `slot-${tier}`, true));
}

/** The SPIN button, dead in flight and dead below the stake. */
function spinHtml(ctx: GameCtx): string {
  const stake = stakeOf(ctx);
  const on = !inFlight(ctx) && stake > 0 && stake <= ctx.credits;
  return part('spin', 'class="casino-bar"', ctl('spin', S.slots_spin, on, 'primary'));
}

export const slotsView: GameView = {
  html(ctx: GameCtx): string {
    // The parts of the machine (`gameview.ts`). `reels` is the one that
    // holds an animation, so it stands alone: the win line, the pills and
    // the handle each rewrite their own part and the strips travel on.
    return `<div class="casino-felt slots${ctx.still ? ' still' : ''}">`
      + part('reels', 'class="felt"', reelsHtml(ctx))
      + winHtml(ctx)
      + pillsHtml(ctx, tierOf(ctx))
      + spinHtml(ctx)
      + paytableHtml(ctx)
      + (ctx.rules ? part('rtp', 'class="note"', S.slots_rtp({ pct: pct(ctx.rules.slotsRtpPct) })) : '')
      + part('pulls', 'class="note pulls"', S.slots_pulls({ count: pulls }))
      + '</div>';
  },

  act(op: string, _btn: HTMLButtonElement, _key: string, ctx: GameCtx): boolean {
    if (op !== 'spin') return false;
    pull(ctx);
    return true;
  },

  key(id: MapKeyId, ctx: GameCtx): boolean {
    if (id !== 'casino_spin') return false;
    return pull(ctx);
  },
};

/**
 * One pull of the handle. It refuses a second stake while a spin is in
 * flight and a stake the reader cannot pay, and it answers whether it
 * sent anything at all.
 */
function pull(ctx: GameCtx): boolean {
  if (inFlight(ctx)) return false;
  const stake = stakeOf(ctx);
  if (stake <= 0 || stake > ctx.credits) return false;
  spinning = true;
  spinAtMs = ctx.nowMs;
  spinFromSeq = ctx.slice.resultSeq;
  pulls += 1;
  ctx.send({ kind: 'spin', stakeTier: tierOf(ctx) });
  return true;
}

/** Puts the screen's own state back. For a test alone. */
export function resetSlotsState(): void {
  spinning = false;
  spinAtMs = 0;
  spinFromSeq = 0;
  pulls = 0;
}
